import { useTranslation, Trans } from 'react-i18next'
import { Truck } from 'lucide-react'
import WizardModal, { inputCls, selectCls, labelCls } from './WizardModal'
import { useWS } from '../contexts/WSContext'
import { apiError } from '../lib/api'

// Wizard de cadastro de Veículo (modelo/catálogo): 1) dados (nome, tipo,
// fabricante, checklist) e 2) revisão. As fotos do checklist são por item, no
// cadastro de Checklists. A máquina física nasce na adoção; aqui é o catálogo.
export default function ModelWizard({ open, onClose, editing = null }) {
  const { t } = useTranslation()
  const { categories, checklists, reloadAdmin } = useWS()

  const initial = {
    name: editing?.name || '',
    categoryId: editing?.category_id ? String(editing.category_id) : (categories[0] ? String(categories[0].id) : ''),
    manufacturer: editing?.manufacturer || '',
    ratedPayload: editing?.rated_payload_t != null ? String(editing.rated_payload_t) : '',
    checklistId: editing?.checklist_id ? String(editing.checklist_id) : '',
  }

  const steps = [
    {
      title: t('admin:fleet.wizard.step_data'),
      valid: x => x.name.trim() !== '' && x.categoryId !== '',
      render: (x, set) => (
        <div className="space-y-4">
          <label className="block"><span className={labelCls}>{t('admin:fleet.wizard.name')}</span>
            <input value={x.name} onChange={e => set({ ...x, name: e.target.value })} placeholder={t('admin:fleet.wizard.name_ph')} className={inputCls} autoFocus />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={labelCls}>{t('admin:fleet.wizard.machine_type')}</span>
              {categories.length === 0 ? (
                <p className="mt-1 text-xs text-status-stop bg-status-stop/10 rounded-md px-3 py-2">
                  <Trans i18nKey="admin:fleet.wizard.no_types" components={[<strong key="0" />]} />
                </p>
              ) : (
                <select value={x.categoryId} onChange={e => set({ ...x, categoryId: e.target.value })} className={selectCls}>
                  <option value="" disabled>{t('admin:select.generic')}</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </label>
            <label className="block"><span className={labelCls}>{t('admin:fleet.fields.manufacturer')}</span>
              <input value={x.manufacturer} onChange={e => set({ ...x, manufacturer: e.target.value })} placeholder={t('admin:fleet.fields.manufacturer_ph')} className={inputCls} />
            </label>
          </div>
          <label className="block"><span className={labelCls}>{t('admin:fleet.wizard.rated_payload')}</span>
            <input value={x.ratedPayload} onChange={e => set({ ...x, ratedPayload: e.target.value })}
              placeholder={t('admin:fleet.wizard.rated_payload_ph')} inputMode="decimal" className={inputCls + ' tabular-nums'} />
          </label>
          <label className="block"><span className={labelCls}>{t('admin:fleet.fields.checklist')}</span>
            {checklists.length === 0 ? (
              <p className="mt-1 text-xs text-status-stop bg-status-stop/10 rounded-md px-3 py-2">
                <Trans i18nKey="admin:fleet.wizard.no_checklists" components={[<strong key="0" />]} />
              </p>
            ) : (
              <select value={x.checklistId} onChange={e => set({ ...x, checklistId: e.target.value })} className={selectCls}>
                <option value="">{t('admin:fleet.wizard.no_checklist_option')}</option>
                {checklists.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">{t('admin:fleet.wizard.checklist_hint')}</p>
          </label>
        </div>
      ),
    },
    {
      title: t('admin:fleet.wizard.step_review'),
      valid: () => true,
      render: (x) => {
        const cat = categories.find(c => String(c.id) === x.categoryId)
        const ck = checklists.find(c => String(c.id) === x.checklistId)
        const ckItems = Array.isArray(ck?.items) ? ck.items.length : 0
        return (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 text-sm">
              <Row k={t('admin:fleet.fields.model')} v={x.name} />
              <Row k={t('admin:fleet.wizard.review_type')} v={cat?.name || '-'} />
              <Row k={t('admin:fleet.fields.manufacturer')} v={x.manufacturer || '-'} />
              <Row k={t('admin:fleet.wizard.review_payload')} v={x.ratedPayload !== '' ? `${x.ratedPayload} ${t('common:units.tons')}` : '-'} />
              <Row k={t('admin:fleet.wizard.review_checklist')} v={ck?.name || t('admin:fleet.wizard.no_checklist')} />
              {ck && <Row k={t('admin:fleet.wizard.review_checklist_items')} v={`${ckItems} · v${ck.version || 1}`} />}
            </div>
          </div>
        )
      },
    },
  ]

  const finish = async (f) => {
    const body = {
      name: f.name.trim(),
      category_id: Number(f.categoryId),
      manufacturer: f.manufacturer.trim(),
      rated_payload_t: f.ratedPayload !== '' && !Number.isNaN(Number(f.ratedPayload)) ? Number(f.ratedPayload) : null,
      checklist_id: f.checklistId ? Number(f.checklistId) : null,
      images: {},
    }
    const url = editing ? `/api/equipment-models/${editing.id}` : '/api/equipment-models'
    const res = await fetch(url, { method: editing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    if (!res.ok) throw await apiError(res)
    reloadAdmin()
  }

  return (
    <WizardModal
      open={open}
      onClose={onClose}
      title={editing ? t('admin:fleet.edit_vehicle') : t('admin:fleet.add_vehicle')}
      icon={<Truck size={18} className="text-brand" />}
      steps={steps}
      initial={initial}
      onFinish={finish}
      finishLabel={editing ? t('common:actions.save') : t('admin:actions.register')}
    />
  )
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between px-3 py-2">
      <span className="text-xs text-slate-500 dark:text-slate-400">{k}</span>
      <span className="text-sm text-slate-700 dark:text-slate-200 font-medium">{v}</span>
    </div>
  )
}
